import { useCallback, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";

import type { FramingSceneInspector } from "../framingInspector";
import {
  buildFramingTimeline,
  clampTimeForNavigation,
  nextTimelinePoint,
  previousTimelinePoint,
} from "./framingTimelineMath";

/**
 * Scene-relative crop/effect/QC timeline (CROP-WB-010). Observed keyframes
 * (diamonds) and interpolated spans (dashed) stay visually and textually
 * distinct; truncated history is labeled incomplete.
 */
export function FramingTimeline({
  inspector,
  timeSeconds,
  onTimeChange,
  feedbackSceneRelativeSeconds,
  actualMediaTimeSeconds,
}: {
  inspector: FramingSceneInspector;
  timeSeconds: number;
  onTimeChange: (timeSeconds: number) => void;
  feedbackSceneRelativeSeconds?: number | null;
  /** Presented media time reported by requestVideoFrameCallback, if any. */
  actualMediaTimeSeconds?: number | null;
}) {
  const model = useMemo(
    () =>
      buildFramingTimeline(inspector, {
        feedbackSceneRelativeSeconds,
      }),
    [feedbackSceneRelativeSeconds, inspector],
  );
  const [outOfRangeNote, setOutOfRangeNote] = useState<string | null>(null);
  const duration = Math.max(model.durationSeconds, 0.001);

  const seek = useCallback(
    (target: number) => {
      const { clamped, outOfRange } = clampTimeForNavigation(target, duration);
      setOutOfRangeNote(
        outOfRange
          ? `Requested ${target.toFixed(3)}s is outside this scene; showing ${clamped.toFixed(3)}s.`
          : null,
      );
      onTimeChange(clamped);
    },
    [duration, onTimeChange],
  );

  const goTo = useCallback(
    (kind: "anomaly" | "keyframe", direction: "next" | "previous") => {
      const point =
        direction === "next"
          ? nextTimelinePoint(model, timeSeconds, kind)
          : previousTimelinePoint(model, timeSeconds, kind);
      if (point) seek(point.timeSeconds);
    },
    [model, seek, timeSeconds],
  );

  const drift =
    actualMediaTimeSeconds != null
      ? Math.abs(actualMediaTimeSeconds - timeSeconds)
      : null;

  return (
    <section aria-label="Scene timeline" className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex grow items-center gap-2 text-xs">
          <span className="sr-only">Scene-relative time</span>
          <input
            type="range"
            className="w-full"
            min={0}
            max={duration}
            step={1 / 120}
            value={Math.min(timeSeconds, duration)}
            onChange={(event) => seek(Number(event.target.value))}
            aria-valuetext={`${timeSeconds.toFixed(2)} seconds`}
          />
        </label>
        <span className="font-mono text-xs">
          {timeSeconds.toFixed(2)}s / {duration.toFixed(2)}s
        </span>
        {model.sceneStartSeconds != null ? (
          <span className="text-xs text-muted-foreground">
            final video {(model.sceneStartSeconds + timeSeconds).toFixed(2)}s
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-1 text-xs">
        <Button
          size="sm"
          variant="outline"
          onClick={() => goTo("anomaly", "previous")}
        >
          <ChevronLeft className="size-3" /> anomaly
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => goTo("anomaly", "next")}
        >
          anomaly <ChevronRight className="size-3" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => goTo("keyframe", "previous")}
        >
          <ChevronLeft className="size-3" /> keyframe
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => goTo("keyframe", "next")}
        >
          keyframe <ChevronRight className="size-3" />
        </Button>
        {drift != null && drift > 1 / 30 ? (
          <span className="text-amber-600" role="note">
            requested {timeSeconds.toFixed(3)}s, presented{" "}
            {actualMediaTimeSeconds!.toFixed(3)}s
          </span>
        ) : null}
        {outOfRangeNote ? (
          <span className="text-amber-600" role="note">
            {outOfRangeNote}
          </span>
        ) : null}
      </div>
      <div className="space-y-1">
        {model.lanes.map((lane) => (
          <div key={lane.kind} className="flex items-center gap-2 text-[11px]">
            <span className="w-36 shrink-0 text-muted-foreground">
              {lane.label}
              {lane.incomplete ? " (incomplete)" : ""}
            </span>
            <svg
              className="h-5 w-full"
              viewBox={`0 0 ${duration} 1`}
              preserveAspectRatio="none"
              role="img"
              aria-label={`${lane.label}: ${lane.points.length} points${
                lane.incomplete ? ", history truncated" : ""
              }`}
            >
              {lane.segments.map((segment, index) => (
                <line
                  key={index}
                  x1={segment.startSeconds}
                  x2={segment.endSeconds}
                  y1={0.5}
                  y2={0.5}
                  stroke="currentColor"
                  strokeWidth={0.14}
                  strokeDasharray={
                    segment.kind === "interpolated"
                      ? `${duration / 60}`
                      : undefined
                  }
                  opacity={segment.kind === "interpolated" ? 0.5 : 0.9}
                  vectorEffect="non-scaling-stroke"
                />
              ))}
              {lane.points.map((point, index) => (
                <g key={index}>
                  {point.kind === "observed_keyframe" ? (
                    <rect
                      x={point.timeSeconds - duration / 200}
                      y={0.28}
                      width={duration / 100}
                      height={0.44}
                      transform={`rotate(45 ${point.timeSeconds} 0.5)`}
                      fill={point.anomaly ? "#dc2626" : "currentColor"}
                    >
                      <title>{point.label}</title>
                    </rect>
                  ) : (
                    <circle
                      cx={Math.min(Math.max(point.timeSeconds, 0), duration)}
                      cy={0.5}
                      r={duration / 120}
                      fill={point.anomaly ? "#dc2626" : "#f59e0b"}
                    >
                      <title>{point.label}</title>
                    </circle>
                  )}
                </g>
              ))}
            </svg>
          </div>
        ))}
        {model.historyTruncated ? (
          <p className="text-[11px] text-amber-600" role="note">
            History was truncated at the read cap; this timeline is incomplete
            and gaps are real gaps.
          </p>
        ) : null}
      </div>
      <details>
        <summary className="cursor-pointer text-[11px] text-muted-foreground">
          Timeline points as a list
        </summary>
        <ul className="mt-1 space-y-0.5 text-[11px]">
          {model.lanes.flatMap((lane) =>
            lane.points.map((point, index) => (
              <li key={`${lane.kind}-${index}`}>
                <button
                  type="button"
                  className="underline-offset-2 hover:underline"
                  onClick={() => seek(point.timeSeconds)}
                >
                  {point.timeSeconds.toFixed(2)}s — {lane.label}: {point.label}
                  {point.anomaly ? " (anomaly)" : ""}
                </button>
              </li>
            )),
          )}
        </ul>
      </details>
    </section>
  );
}
